interface ParameterComponent {
  name: string;
  type: string;
  components?: ParameterComponent[];
}

interface Parameter {
  name: string;
  type: string;
  components?: ParameterComponent[];
}

// Standard authData structure that's always the first parameter
const AUTH_DATA_PARAMETER: Parameter = {
  name: "authData",
  type: "tuple",
  components: [
    {
      name: "nonce",
      type: "uint256"
    },
    {
      name: "expiry",
      type: "uint256"
    },
    {
      name: "id",
      type: "bytes32"
    },
    {
      name: "executions",
      type: "tuple[]",
      components: [
        {
          name: "id",
          type: "bytes32"
        },
        {
          name: "request",
          type: "bytes"
        },
        {
          name: "response",
          type: "bytes"
        }
      ]
    },
    {
      name: "result",
      type: "bytes"
    },
    {
      name: "sponsorExecutionFee",
      type: "bool"
    },
    {
      name: "signature",
      type: "bytes"
    }
  ]
};

// Common parameter name mappings for different types
const TYPE_NAME_MAPPINGS: Record<string, string> = {
  'uint256': 'amount',
  'address': 'targetAddress',
  'string': 'data',
  'bytes': 'data',
  'bytes32': 'hash',
  'bool': 'flag'
};

/**
 * Parses a function signature and extracts parameter types
 * Example: "purchaseTokens((uint256,uint256,bytes32,(bytes32,bytes,bytes)[],bytes,bool,bytes),uint256)"
 * Returns: ["(uint256,uint256,bytes32,(bytes32,bytes,bytes)[],bytes,bool,bytes)", "uint256"]
 */
function extractParameterTypes(functionSignature: string): string[] {
  // Extract function name and parameters part
  const match = functionSignature.match(/^(\w+)\((.*)\)$/);
  if (!match) {
    throw new Error('Invalid function signature format');
  }

  const parametersString = match[2];
  if (!parametersString) {
    return [];
  }

  const parameters: string[] = [];
  let currentParam = '';
  let depth = 0;
  let inArray = false;

  for (let i = 0; i < parametersString.length; i++) {
    const char = parametersString[i];

    if (char === '(') {
      depth++;
      currentParam += char;
    } else if (char === ')') {
      depth--;
      currentParam += char;
    } else if (char === '[') {
      inArray = true;
      currentParam += char;
    } else if (char === ']') {
      inArray = false;
      currentParam += char;
    } else if (char === ',' && depth === 0 && !inArray) {
      // Found a parameter separator at root level
      parameters.push(currentParam.trim());
      currentParam = '';
    } else {
      currentParam += char;
    }
  }

  // Add the last parameter
  if (currentParam.trim()) {
    parameters.push(currentParam.trim());
  }

  return parameters;
}

/**
 * Generates a parameter name based on type and index
 */
function generateParameterName(type: string, index: number): string {
  // Remove array notation for name generation
  const baseType = type.replace(/\[\]$/, '');
  const baseName = TYPE_NAME_MAPPINGS[baseType] || 'param';
  
  // If it's the first additional parameter (index 1, since 0 is authData), don't add number
  if (index === 1) {
    return baseName;
  }
  
  return `${baseName}${index}`;
}

/**
 * Checks if a parameter type matches the expected authData tuple structure
 */
function isAuthDataTuple(paramType: string): boolean {
  // The expected authData pattern
  const authDataPattern = /^\(uint256,uint256,bytes32,\(bytes32,bytes,bytes\)\[\],bytes,bool,bytes\)$/;
  return authDataPattern.test(paramType);
}

/**
 * Converts a simple type to a parameter object
 */
function createSimpleParameter(type: string, name: string): Parameter {
  return {
    name,
    type
  };
}

/**
 * Generates structured inputs array based on parameters
 */
export function generateInputsFromParameters(parameters: Parameter[]): any[] {
  // Build a single value object with keys matching parameter names
  const valueObject: Record<string, any> = {};

  parameters.forEach(param => {
    if (param.name === 'authData') {
      valueObject['authData'] = {
        nonce: "${prepare-authdata.nonce}",
        expiry: "${prepare-authdata.expiry}",
        id: "${prepare-authdata.id}",
        executions: [],
        result: "${prepare-authdata.result}",
        sponsorExecutionFee: "${prepare-authdata.sponsor_execution_fee}",
        signature: "${prepare-authdata.signature}"
      };
    } else {
      valueObject[param.name] = getDefaultValueForType(param.type);
    }
  });

  // Return as a single input named "value"
  return [
    {
      name: 'value',
      type: 'object',
      value: valueObject,
      required: true,
      description: 'Function parameters object'
    }
  ];
}

/**
 * Maps parameter types to input field types
 */
function mapTypeToInputType(type: string): string {
  const baseType = type.replace(/\[\]$/, '');
  
  switch (baseType) {
    case 'uint256':
    case 'uint128':
    case 'uint64':
    case 'uint32':
    case 'uint16':
    case 'uint8':
      return 'uint256';
    case 'address':
      return 'address';
    case 'bytes32':
      return 'bytes32';
    case 'bytes':
      return 'bytes';
    case 'string':
      return 'string';
    case 'bool':
      return 'bool';
    default:
      if (type.includes('[]')) {
        return 'object[]';
      }
      return 'object';
  }
}


/**
 * Gets default value for a parameter type
 */
function getDefaultValueForType(type: string): any {
  const baseType = type.replace(/\[\]$/, '');
  
  switch (baseType) {
    case 'uint256':
    case 'uint128':
    case 'uint64':
    case 'uint32':
    case 'uint16':
    case 'uint8':
      return "0";
    case 'int256':
    case 'int128':
    case 'int64':
    case 'int32':
    case 'int16':
    case 'int8':
      return "0";
    case 'address':
      return "0x0000000000000000000000000000000000000000";
    case 'bool':
      return false;
    case 'bytes32':
      return "0x0000000000000000000000000000000000000000000000000000000000000000";
    case 'bytes':
      return "0x";
    case 'string':
      return "";
    default:
      // For arrays, return empty array
      if (type.includes('[]')) {
        return [];
      }
      return "";
  }
}

/**
 * Main function to parse function signature and generate parameters
 */
export function parseFunctionSignature(functionSignature: string): Parameter[] {
  try {
    const parameterTypes = extractParameterTypes(functionSignature);
    const parameters: Parameter[] = [];

    // First parameter should always be authData tuple
    if (parameterTypes.length > 0 && isAuthDataTuple(parameterTypes[0])) {
      parameters.push(AUTH_DATA_PARAMETER);
    } else if (parameterTypes.length > 0) {
      // If first parameter doesn't match authData pattern, still add it but warn
      console.warn('First parameter does not match expected authData tuple structure');
      parameters.push(AUTH_DATA_PARAMETER);
    }

    // Process additional parameters
    for (let i = 1; i < parameterTypes.length; i++) {
      const paramType = parameterTypes[i];
      const paramName = generateParameterName(paramType, i);
      parameters.push(createSimpleParameter(paramType, paramName));
    }

    return parameters;
  } catch (error) {
    console.error('Error parsing function signature:', error);
    // Return at least the authData parameter as fallback
    return [AUTH_DATA_PARAMETER];
  }
}

/**
 * Main function to parse function signature and generate both parameters and inputs
 */
export function parseFunctionSignatureWithInputs(functionSignature: string): { parameters: Parameter[], inputs: any[] } {
  const parameters = parseFunctionSignature(functionSignature);
  const inputs = generateInputsFromParameters(parameters);
  
  return { parameters, inputs };
}


/**
 * Extracts function name from signature
 */
export function extractFunctionName(functionSignature: string): string {
  const match = functionSignature.match(/^(\w+)\(/);
  return match ? match[1] : 'unknown';
}